import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Center,
  Text,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Radio,
  RadioGroup,
  Image,
  Flex,
  SimpleGrid,
  useToast,
  CloseButton,
  Tooltip,
  Heading,
} from "@chakra-ui/react";
import { ViewIcon } from "@chakra-ui/icons";
import {
  fetchInventoryVersions,
  fetchInventoryVersionsById,
  markAsDeliverable,
} from "../../../../ProjectBoardServices/BoardModalAPI/LeftModalAPI/RelatedDesignsTabAPI";
import AddProductButtonModal from "../../../../../ProductInventory/TableComponents/Header/Components/AddProductButtonModal";

const RelatedDesigns = ({ task, onDeliverableMarked }) => {
  const history = useHistory();
  const toast = useToast();

  const tableBg = useColorModeValue("white", "gray.800");
  const rowHoverBg = useColorModeValue("blue.50", "gray.700");

  const [relatedDesigns, setRelatedDesigns] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedImage, setSelectedImage] = useState("0");
  const [versionImages, setVersionImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [loadingDeliverable, setLoadingDeliverable] = useState(false);

  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [zoomImageUrl, setZoomImageUrl] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!task?.id) return;
      try {
        const response = await fetchInventoryVersions(task.id, 0);
        console.log("Initial Related Designs:", response.data);
        if (response.data.status && response.data.data.length > 0) {
          const items = response.data.data.map((item) => ({
            id: item.id,
            parent_id: item.parent_id,
            productCode: item.productCode || "N/A",
            version: item.version || "v1.0",
            selectedBy: "Unknown",
            selectedDate: "N/A",
            status: "In Progress",
          }));
          setRelatedDesigns(items);
        }
      } catch (error) {
        console.error("Table API Error:", error);
      }
    };
    fetchData();
  }, [task?.id]);

  const handleRowClick = async (entry) => {
    setSelectedEntry(entry);
    setSelectedImage("0");
    setIsOpen(true);
    setLoadingImages(true);
    try {
      const parentId = entry.parent_id || entry.id;
      const res = await fetchInventoryVersionsById(parentId);
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        const firstVersion = res.data[0];
        const latestOutput = firstVersion.latest_output;
        if (latestOutput) {
          setVersionImages([{
            deliverableId: firstVersion.id,
            id: latestOutput.id,
            url: latestOutput.file_path,
            name: latestOutput.original_name || "Latest Output",
            extension: latestOutput.extension,
            type: latestOutput.file_type,
          }]);
        } else {
          setVersionImages([]);
        }
      } else {
        setVersionImages([]);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load versions", status: "error", duration: 3000, isClosable: true });
      setVersionImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedEntry(null);
    setSelectedImage("0");
    setVersionImages([]);
  };

  const openZoom = (url) => { setZoomImageUrl(url); setIsZoomOpen(true); };
  const closeZoom = () => { setIsZoomOpen(false); setZoomImageUrl(null); };

  const handleMarkAsDeliverable = async () => {
    if (versionImages.length === 0) {
      toast({ title: "Error", description: "No version selected", status: "warning", duration: 2000, isClosable: true });
      return;
    }
    setLoadingDeliverable(true);
    try {
      const index = parseInt(selectedImage, 10);
      const selectedOutput = versionImages[index];
      const response = await markAsDeliverable(task.id, selectedOutput.deliverableId, selectedOutput.id);
      toast({ title: "Success!", description: `Marked as deliverable: ${selectedOutput.name}`, status: "success", duration: 4000, isClosable: true });
      if (typeof onDeliverableMarked === "function") onDeliverableMarked();
      handleClose();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.message || "Failed to mark as deliverable", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoadingDeliverable(false);
    }
  };

  const handleCreate = () => {
    const index = parseInt(selectedImage, 10);
    const selected = versionImages[index];
    toast({ title: "Create", description: `Creating from: ${selected?.name || `version ${index + 1}`}`, status: "info", duration: 3000, isClosable: true });
    handleClose();
  };

  return (
    <Box
      p={5}
      bg={tableBg}
      borderRadius="lg"
      shadow="sm"
      border="1px solid"
      borderColor="gray.100"
    >
      {/* ── Page heading ── */}
      <Heading as="h3" size="md" mb={4} color="blue.600" fontWeight="600">
        Related Designs
      </Heading>

      {/* ── Add button ── */}
      <Box mb={4}>
        <AddProductButtonModal />
      </Box>

      {/* ── Table ── */}
      <Box
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        overflow="hidden"
      >
        <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            style={{ tableLayout: "fixed", width: "100%", minWidth: "560px" }}
          >
            <Thead bg="blue.500">
              <Tr>
                <Th color="white" fontSize="xs" py={3} w="25%">
                  Product Code
                </Th>
                <Th color="white" fontSize="xs" py={3} w="15%">
                  Version
                </Th>
                <Th color="white" fontSize="xs" py={3} w="22%">
                  Selected By
                </Th>
                <Th color="white" fontSize="xs" py={3} w="22%">
                  Selected Date
                </Th>
                <Th color="white" fontSize="xs" py={3} w="16%" textAlign="center">
                  Action
                </Th>
              </Tr>
            </Thead>

            <Tbody>
              {relatedDesigns.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={10}>
                    <Flex direction="column" align="center" gap={2}>
                      <Text fontSize="2xl">🎨</Text>
                      <Text color="gray.400" fontSize="sm">
                        No designs found
                      </Text>
                    </Flex>
                  </Td>
                </Tr>
              ) : (
                relatedDesigns.map((entry) => (
                  <Tr
                    key={entry.id}
                    _hover={{ bg: rowHoverBg, cursor: "pointer" }}
                    transition="background 0.15s"
                  >
                    {/* Product Code — full text, no truncation */}
                    <Td>
                      <Tooltip label={entry.productCode} placement="top" hasArrow openDelay={400}>
                        <Text
                          fontSize="xs"
                          fontWeight="500"
                          color="gray.800"
                          whiteSpace="normal"
                          wordBreak="break-word"
                          lineHeight="1.5"
                        >
                          {entry.productCode}
                        </Text>
                      </Tooltip>
                    </Td>

                    {/* Version */}
                    <Td>
                      <Text fontSize="xs" color="blue.600" fontWeight="500">
                        {entry.version}
                      </Text>
                    </Td>

                    {/* Selected By */}
                    <Td>
                      <Text fontSize="xs" color="gray.600" whiteSpace="normal" wordBreak="break-word">
                        {entry.selectedBy}
                      </Text>
                    </Td>

                    {/* Selected Date */}
                    <Td>
                      <Text fontSize="xs" color="gray.600">
                        {entry.selectedDate}
                      </Text>
                    </Td>

                    {/* Action */}
                    <Td textAlign="center">
                      <Tooltip label="View outputs" placement="top" hasArrow>
                        <IconButton
                          size="sm"
                          icon={<ViewIcon />}
                          colorScheme="blue"
                          variant="ghost"
                          borderRadius="md"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(entry);
                          }}
                          aria-label={`View versions for ${entry.productCode}`}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* ══════════════════════════════
          Main Versions Modal
      ══════════════════════════════ */}
      <Modal isOpen={isOpen} onClose={handleClose} size="4xl" isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          maxH="90vh"
          borderRadius="xl"
          border="0.5px solid"
          borderColor="gray.200"
          overflow="hidden"
        >
          {/* Header */}
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.100"
            py={4}
            px={6}
          >
            <Flex align="center" justify="space-between">
              <Box>
                <Text fontSize="xs" color="gray.400" fontWeight="400" mb={0.5}>
                  Latest Outputs
                </Text>
                <Text fontSize="md" fontWeight="600" color="gray.800" lineHeight="1.3">
                  {selectedEntry?.productCode || "N/A"}
                </Text>
              </Box>
              <CloseButton onClick={handleClose} size="sm" color="gray.500" />
            </Flex>
          </ModalHeader>

          {/* Body */}
          <ModalBody overflowY="auto" p={6}>
            {loadingImages ? (
              <Center py={20} flexDirection="column" gap={3}>
                <Text fontSize="sm" color="gray.500">Loading versions...</Text>
              </Center>
            ) : versionImages.length === 0 ? (
              <Center py={20} flexDirection="column" gap={2}>
                <Text fontSize="2xl">📂</Text>
                <Text color="gray.400" fontSize="sm">No outputs found</Text>
              </Center>
            ) : (
              <RadioGroup value={selectedImage} onChange={setSelectedImage}>
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={5}>
                  {versionImages.map((img, index) => (
                    <Flex
                      key={img.id}
                      align="center"
                      p={4}
                      border="1.5px solid"
                      borderColor={selectedImage === index.toString() ? "blue.400" : "gray.200"}
                      borderRadius="xl"
                      cursor="pointer"
                      onClick={() => setSelectedImage(index.toString())}
                      direction="column"
                      bg={selectedImage === index.toString() ? "blue.50" : "white"}
                      transition="all 0.2s"
                      _hover={{ shadow: "md", borderColor: "blue.300" }}
                      shadow={selectedImage === index.toString() ? "sm" : "none"}
                    >
                      <Radio value={index.toString()} mb={3} size="lg" colorScheme="blue" />

                      {img.extension === "pdf" ? (
                        <Flex
                          w="160px"
                          h="160px"
                          borderRadius="lg"
                          border="1.5px dashed"
                          borderColor="gray.300"
                          align="center"
                          justify="center"
                          bg="gray.50"
                          direction="column"
                          gap={1}
                        >
                          <Text fontSize="3xl">📄</Text>
                          <Text fontSize="xs" color="gray.500" fontWeight="500">PDF File</Text>
                        </Flex>
                      ) : (
                        <Box
                          w="160px"
                          h="160px"
                          borderRadius="lg"
                          overflow="hidden"
                          border="1px solid"
                          borderColor="gray.200"
                          position="relative"
                          cursor="zoom-in"
                          onClick={(e) => {
                            e.stopPropagation();
                            openZoom(img.url);
                          }}
                          _hover={{ "& .zoom-overlay": { opacity: 1 } }}
                        >
                          <Image
                            src={img.url}
                            alt={img.name}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                            fallbackSrc="https://via.placeholder.com/160x160/eee/999?text=No+Image"
                          />
                          <Flex
                            className="zoom-overlay"
                            position="absolute"
                            inset={0}
                            bg="blackAlpha.500"
                            align="center"
                            justify="center"
                            opacity={0}
                            transition="opacity 0.2s"
                            borderRadius="lg"
                          >
                            <Text fontSize="xl" color="white">🔍</Text>
                          </Flex>
                        </Box>
                      )}

                      {/* File name — full, wraps properly */}
                      <Text
                        mt={3}
                        fontWeight="500"
                        fontSize="xs"
                        color="gray.700"
                        textAlign="center"
                        whiteSpace="normal"
                        wordBreak="break-word"
                        lineHeight="1.5"
                        w="100%"
                      >
                        {img.name}
                      </Text>
                    </Flex>
                  ))}
                </SimpleGrid>
              </RadioGroup>
            )}
          </ModalBody>

          {/* Footer */}
          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.100"
            py={4}
            px={6}
            gap={2}
          >
            <Button
              colorScheme="green"
              size="sm"
              px={5}
              onClick={handleMarkAsDeliverable}
              isLoading={loadingDeliverable}
              isDisabled={versionImages.length === 0}
              borderRadius="md"
            >
              Mark as Deliverable
            </Button>
            <Button
              variant="outline"
              size="sm"
              px={5}
              onClick={handleClose}
              borderRadius="md"
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ══════════════════════════════
          Full-screen Zoom Modal
      ══════════════════════════════ */}
      <Modal isOpen={isZoomOpen} onClose={closeZoom} size="full" motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.900" />
        <ModalContent bg="transparent" m={0} maxW="100vw" maxH="100vh">
          <ModalHeader p={0}>
            <CloseButton
              size="lg"
              color="white"
              position="absolute"
              top={4}
              right={4}
              zIndex={10}
              onClick={closeZoom}
            />
          </ModalHeader>
          <ModalBody
            p={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100vh"
          >
            {zoomImageUrl && (
              <Image
                src={zoomImageUrl}
                alt="Zoomed design"
                maxW="95vw"
                maxH="95vh"
                objectFit="contain"
                onClick={closeZoom}
                cursor="zoom-out"
                transition="transform 0.25s ease"
                _hover={{ transform: "scale(1.03)" }}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default RelatedDesigns;